import React, { useState, useRef, useCallback } from 'react';
import { File, Image, Video, AudioWaveform, FileText, AlertCircle, CloudUpload } from 'lucide-react';

interface MediaUploadProps {
  onUpload: (file: File) => Promise<void>;
  onCancel?: () => void;
  disabled?: boolean;
  className?: string;
  maxSizeMB?: number;
  allowedTypes?: string[];
  primaryColor?: string;
}

interface UploadState {
  isDragOver: boolean;
  isUploading: boolean;
  progress: number;
  error?: string;
  selectedFile?: File;
  previewUrl?: string;
}

const MediaUpload: React.FC<MediaUploadProps> = ({
  onUpload,
  onCancel,
  disabled = false,
  className = '',
  maxSizeMB = 100,
  allowedTypes = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp',
    'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'video/mp4', 'video/quicktime', 'video/x-msvideo',
    'audio/mpeg', 'audio/wav', 'audio/x-m4a'
  ],
  primaryColor
}) => {
  const [state, setState] = useState<UploadState>({
    isDragOver: false,
    isUploading: false,
    progress: 0
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <Image size={24} className="text-blue-500" />;
    if (type.startsWith('video/')) return <Video size={24} className="text-purple-500" />;
    if (type.startsWith('audio/')) return <AudioWaveform size={24} className="text-green-500" />;
    if (type.includes('pdf') || type.includes('document') || type.includes('text'))
      return <FileText size={24}  />;
    return <File size={24} />;
  };

  const validateFile = useCallback((file: File): string | null => {
    // Check file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return `File size must be less than ${maxSizeMB}MB`;
    }

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return 'This file type is not supported';
    }

    return null;
  }, [maxSizeMB, allowedTypes]);

  const createPreviewUrl = (file: File): string | undefined => {
    if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
      return URL.createObjectURL(file);
    }
    return undefined;
  };

  const handleFileSelect = useCallback((file: File) => {
    const error = validateFile(file);

    if (error) {
      setState(prev => ({ ...prev, error, selectedFile: undefined, previewUrl: undefined }));
      return;
    }

    const previewUrl = createPreviewUrl(file);

    setState(prev => ({
      ...prev,
      selectedFile: file,
      previewUrl,
      error: undefined
    }));
  }, [validateFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setState(prev => ({ ...prev, isDragOver: true }));
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setState(prev => ({ ...prev, isDragOver: false }));
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setState(prev => ({ ...prev, isDragOver: false }));

    if (disabled || !e.dataTransfer.files.length) return;

    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  }, [disabled, handleFileSelect]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleUpload = async () => {
    if (!state.selectedFile || disabled) return;

    setState(prev => ({ ...prev, isUploading: true, progress: 0, error: undefined }));

    try {
      await onUpload(state.selectedFile);

      // Clear state after successful upload
      setState({
        isDragOver: false,
        isUploading: false,
        progress: 0,
        selectedFile: undefined,
        previewUrl: undefined
      });

      // Clear preview URL to prevent memory leaks
      if (state.previewUrl) {
        URL.revokeObjectURL(state.previewUrl);
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        isUploading: false,
        progress: 0,
        error: error instanceof Error ? error.message : 'Upload failed'
      }));
    }
  };

  const handleCancel = () => {
    if (state.previewUrl) {
      URL.revokeObjectURL(state.previewUrl);
    }

    setState({
      isDragOver: false,
      isUploading: false,
      progress: 0,
      selectedFile: undefined,
      previewUrl: undefined,
      error: undefined
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    onCancel?.();
  };



  return (
    <div className={`media-upload ${className}`}>
      {!state.selectedFile ? (
        // File selection area
        <div
          className={`
            relative border-2 border-dashed rounded-md text-center transition-colors cursor-pointer p-4
            ${state.isDragOver
              ? 'border-blue-500 bg-blue-50 dark:border-gray-600 dark:bg-gray-700'
              : 'border-gray-200 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={allowedTypes.join(',')}
            onChange={handleFileInputChange}
            disabled={disabled}
            className="hidden"
            name="askMediaId"
          />

          <CloudUpload size={32} className={`mx-auto ${state.isDragOver ? 'text-blue-500' : 'text-gray-600 dark:text-white'}`} />

          <p className=" font-medium text-gray-700 dark:text-white mb-1">
            {state.isDragOver ? 'Drop your file here' : 'Upload a file'}
          </p>

          <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">
            Drag and drop or click to select a file
          </p>

          <p className="text-[10px] text-gray-400">
            Max size: {maxSizeMB}MB • Supported: Images, Videos, Documents, Audio
          </p>
        </div>
      ) : (
        // File preview and upload area
        <div className="space-y-4">
          {/* File preview */}
          <div className="relative group">
            <div className="relative overflow-hidden">
              <div className="flex items-center justify-center min-h-[140px] max-h-[140px]">
                {state.previewUrl && state.selectedFile.type.startsWith('image/') ? (
                  <div className="relative w-full h-[140px]">
                    <img
                      src={state.previewUrl}
                      alt="Preview"
                      className="w-full h-full object-cover rounded-lg shadow-md"
                    />
                    <div className="absolute inset-0 bg-black/10 rounded-lg" />
                  </div>
                ) : state.previewUrl && state.selectedFile.type.startsWith('video/') ? (
                  <div className="relative w-full h-[120px]">
                    <video
                      src={state.previewUrl}
                      className="w-full h-full object-cover rounded-lg shadow-md"
                      muted
                      autoPlay
                      loop
                    />
                    <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-lg">
                      VIDEO
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-4 p-6 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-800/50 dark:border-gray-700">
                    <div className="p-2 bg-white dark:bg-gray-600 rounded-sm flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-200 text-gray-900 dark:text-white">
                      {getFileIcon(state.selectedFile.type)}
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate max-w-[220px] group-hover:text-gray-900 transition-colors">
                        {state.selectedFile.name}
                      </p>
                      {/* <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-gray-500 uppercase tracking-wider font-medium bg-gray-100 px-2 py-1 rounded">
                          {state.selectedFile.type.split('/')[1] || 'FILE'}
                        </span>
                        <span className="text-xs text-gray-400">
                          {(state.selectedFile.size / 1024 / 1024).toFixed(1)} MB
                        </span>
                      </div> */}
                    </div>
                  </div>
                )}
              </div>

              {/* Upload progress overlay */}
              {state.isUploading && (
               <div className="mt-3">
                <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                  <span>Uploading...</span>
                  <span>{Math.round(state.progress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${state.progress}%` }}
                  />
                </div>
              </div>
              )}
            </div>
          </div>

          {/* Error message */}
          {state.error && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl shadow-sm">
              <div className="shrink-0 w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mt-0.5">
                <AlertCircle size={12} className="text-red-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">{state.error}</p>
                <p className="text-xs text-red-600 mt-1">Please try again or select a different file</p>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 w-full">
            <button
              onClick={handleUpload}
              disabled={state.isUploading || disabled}
              data-testid='send-file-button'
              className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-sm hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-100 transition-all duration-200 shadow-sm hover:shadow-md"
              style={{ backgroundColor: primaryColor }}
            >
              {state.isUploading ? (
                <span className="flex items-center justify-center gap-2">
                  Sending...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Send File
                </span>
              )}
            </button>

            <button
              onClick={handleCancel}
              disabled={state.isUploading}
              className="w-max px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaUpload;